import React from 'react';
import Image from 'next/image';
import { Library } from 'lucide-react';
import { User } from 'firebase/auth';
import { TaskBankIdeasButtons } from './task-bank-ideas-buttons';
import { ScoreButton } from './score-button';
import { ReportPlan, ReportPlanItem } from './report-plan';
import { StructuredWritingData } from '@/types/writing';

import { Task } from '@/app/task-bank/public-actions';

interface ReportEditorSidebarProps {
  hasSelectedTask: boolean;
  selectedTask: Task | null;
  user: User | null;
  prompt: string;
  imageUrl: string;
  showIdeas: boolean;
  setShowIdeas: (show: boolean) => void;
  setTaskBankOpen: (open: boolean) => void;
  isProUser: boolean;
  isScoring: boolean;
  essayData: StructuredWritingData;
  currentEssayId: string | null;
  fetchNewScore: (prompt: string, data: StructuredWritingData) => void;
  canScore: boolean;
  requirements: string[];
  plan: ReportPlanItem[];
  isPlanOpen: boolean;
  onPlanOpenChange: (open: boolean) => void;
  handlePlanContentChange: (idx: number, value: string) => void;
  addPlanItem: () => void;
  removePlanItem: (idx: number) => void;
}

export function ReportEditorSidebar({
  hasSelectedTask,
  selectedTask,
  user,
  prompt,
  imageUrl,
  showIdeas,
  setShowIdeas,
  setTaskBankOpen,
  isProUser,
  isScoring,
  essayData,
  currentEssayId,
  fetchNewScore,
  canScore,
  requirements,
  plan,
  isPlanOpen,
  onPlanOpenChange,
  handlePlanContentChange,
  addPlanItem,
  removePlanItem,
}: ReportEditorSidebarProps) {
  if (!hasSelectedTask) {
    return (
      <div className='flex flex-col space-y-2 flex-shrink-0'>
        <div
          className='flex flex-col items-center justify-center py-12 border-2 border-dashed border-muted rounded-lg bg-muted/40 cursor-pointer transition hover:bg-muted/60'
          onClick={() => setTaskBankOpen(true)}
          role='button'
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') setTaskBankOpen(true);
          }}
        >
          <Library className='h-8 w-8 text-muted-foreground mb-2' />
          <span className='text-lg text-muted-foreground font-semibold'>
            Select a task to begin
          </span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className='flex flex-col sm:flex-row gap-2 flex-shrink-0'>
        <TaskBankIdeasButtons
          user={user}
          selectedTask={selectedTask}
          prompt={prompt}
          showIdeas={showIdeas}
          setShowIdeas={setShowIdeas}
          setTaskBankOpen={setTaskBankOpen}
        />
        {selectedTask && (
          <ScoreButton
            isProUser={isProUser}
            isScoring={isScoring}
            prompt={prompt}
            essayData={essayData}
            currentEssayId={currentEssayId}
            user={user}
            fetchNewScore={(prompt: string, data: StructuredWritingData) =>
              fetchNewScore(prompt, data)
            }
            taskType='task1'
            canScore={canScore}
            requirements={requirements}
          />
        )}
      </div>

      <div className='flex flex-col space-y-4 flex-shrink-0'>
        <div className='w-full border rounded-lg bg-muted p-4 font-bold text-base text-foreground min-h-[80px] whitespace-pre-line'>
          {prompt}
        </div>

        {imageUrl && (
          <div className='w-full border rounded-lg overflow-hidden flex items-center justify-center bg-muted'>
            <Image
              src={imageUrl}
              alt={prompt || 'Task 1 visual'}
              className='max-w-full max-h-[400px] object-contain'
              width={600}
              height={400}
            />
          </div>
        )}

        <ReportPlan
          plan={plan}
          handlePlanContentChange={handlePlanContentChange}
          addPlanItem={addPlanItem}
          removePlanItem={removePlanItem}
          handleKeyDown={undefined}
          isPlanOpen={isPlanOpen}
          onPlanOpenChange={onPlanOpenChange}
        />
      </div>
    </>
  );
}
